import { useEffect, useMemo, useState } from "react";
import { createTheme } from "@mui/material/styles";
import { uiPalette, uiRadii } from "../uiStyles";

const getSystemTheme = () => (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");

const radius = (value) => `${value * 4}px`;
const easing = "cubic-bezier(0.22, 1, 0.36, 1)";

export function buildAppTheme(mode) {
  const p = mode === "dark" ? uiPalette.dark : uiPalette.light;
  return createTheme({
    palette: {
      mode,
      primary: { main: p.primary, dark: p.primaryHover, contrastText: "#fff", disabled: p.primaryDisabled },
      error: { main: p.danger, dark: p.dangerHover, disabled: p.dangerDisabled },
      background: { default: p.background, paper: p.paper },
      text: { primary: p.text, secondary: p.textSecondary },
      divider: p.border,
      ui: p,
    },
    typography: {
      // Content font stays MUI's default stack (Roboto/Helvetica/Arial), as before 1.0.18.
      h5: { fontWeight: 700 },
      h6: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 500 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: p.background, transition: "background-color 0.3s ease" },
          "@keyframes trackerDialogIn": {
            from: { opacity: 0, transform: "translateY(10px) scale(0.97)" },
            to: { opacity: 1, transform: "none" },
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
            },
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius(uiRadii.pill),
            transition: `background-color 0.2s ${easing}, border-color 0.2s ${easing}, color 0.2s ${easing}, transform 0.2s ${easing}`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: `background-color 0.2s ${easing}, border-color 0.2s ${easing}, color 0.2s ${easing}, transform 0.2s ${easing}, box-shadow 0.2s ${easing}`,
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: { tooltip: { borderRadius: radius(uiRadii.item), fontSize: 12 } },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: radius(uiRadii.card),
            border: `1px solid ${p.border}`,
            boxShadow: p.shadowPopover,
          },
        },
      },
      MuiMenu: { styleOverrides: { list: { padding: 4 } } },
      MuiMenuItem: {
        styleOverrides: {
          root: { borderRadius: radius(uiRadii.item), minHeight: 40, transition: `background-color 0.15s ${easing}` },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: { "& .MuiBackdrop-root": { backgroundColor: p.backdrop, backdropFilter: "blur(3px)" } },
          paper: {
            borderRadius: radius(uiRadii.shell),
            border: `1px solid ${p.border}`,
            boxShadow: p.shadowShell,
            animation: `trackerDialogIn 0.28s ${easing}`,
          },
        },
      },
      // Titles often wrap their own Typography heading, so avoid an <h2> around it.
      MuiDialogTitle: { defaultProps: { component: "div" }, styleOverrides: { root: { fontWeight: 700 } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius(uiRadii.field),
            backgroundColor: p.fieldBg,
            transition: "box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: mode === "dark" ? "rgba(255,255,255,0.14)" : "rgba(17,24,39,0.1)" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: mode === "dark" ? "rgba(255,255,255,0.22)" : "rgba(17,24,39,0.16)" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: mode === "dark" ? "rgba(255,255,255,0.32)" : p.primary, borderWidth: 1 },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: mode === "dark" ? "rgba(255,255,255,0.62)" : "rgba(17,24,39,0.58)",
            "&.Mui-focused": { color: mode === "dark" ? "rgba(255,255,255,0.78)" : p.primaryHover },
          },
        },
      },
      MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: radius(uiRadii.card), alignItems: "center" } } },
      MuiCard: { styleOverrides: { root: { borderRadius: radius(uiRadii.card) } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: radius(uiRadii.pill) } } },
    },
  });
}

export default function useAppTheme(initialMode = "light") {
  const [mode, setMode] = useState(initialMode === "system" ? getSystemTheme() : (initialMode === "dark" ? "dark" : "light"));

  useEffect(() => {
    if (initialMode === "system") {
      setMode(getSystemTheme());
      if (typeof window === "undefined" || !window.matchMedia) return undefined;
      const media = window.matchMedia("(prefers-color-scheme: dark)");
      const listener = (event) => setMode(event.matches ? "dark" : "light");
      media.addEventListener?.("change", listener);
      media.addListener?.(listener);
      return () => {
        media.removeEventListener?.("change", listener);
        media.removeListener?.(listener);
      };
    }
    setMode(initialMode === "dark" ? "dark" : "light");
    return undefined;
  }, [initialMode]);

  const colorMode = useMemo(() => ({
    setColorMode: (value) => setMode(value === "dark" ? "dark" : "light"),
    toggleColorMode: () => setMode((prev) => (prev === "light" ? "dark" : "light")),
  }), []);

  const theme = useMemo(() => buildAppTheme(mode), [mode]);

  return { colorMode, theme, mode };
}
